/**
 * Login page — renders OAuth provider buttons.
 * Healing aesthetic: soft card, muted colors, rounded corners.
 */
import { Card } from "@/components/ui/Card";
import { useAuth } from "../hooks/useAuth";
import { cn } from "@/lib/utils";
import { useSearchParams } from "react-router";

/** Provider brand config for icons and colors */
const PROVIDER_UI: Record<
  string,
  { label: string; icon: string; bgClass: string }
> = {
  google: {
    label: "Continue with Google",
    icon: "G",
    bgClass: "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200",
  },
  github: {
    label: "Continue with GitHub",
    icon: "GH",
    bgClass: "bg-gray-900 hover:bg-gray-800 text-white",
  },
  facebook: {
    label: "Continue with Facebook",
    icon: "FB",
    bgClass: "bg-blue-600 hover:bg-blue-700 text-white",
  },
  twitter: {
    label: "Continue with X",
    icon: "X",
    bgClass: "bg-black hover:bg-gray-900 text-white",
  },
};

export function LoginPage() {
  const { providers, isLoading, loginWith } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            Orymia
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to save and share your worlds
          </p>
        </div>

        {/* Card */}
        <Card variant="semantic" padding="sm">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {getErrorLabel(error)}
            </div>
          )}

          {providers.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-4">
              No authentication providers configured.
            </p>
          ) : (
            <div className="space-y-3">
              {providers.map((provider) => {
                const ui = PROVIDER_UI[provider.id] ?? {
                  label: `Continue with ${provider.name}`,
                  icon: provider.id[0].toUpperCase(),
                  bgClass:
                    "bg-primary hover:bg-primary/90 text-primary-foreground",
                };

                return (
                  <button
                    key={provider.id}
                    onClick={() => loginWith(provider.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                      ui.bgClass,
                    )}
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-xs font-bold">
                      {ui.icon}
                    </span>
                    {ui.label}
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Guest link */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Continue as guest
          </a>
        </div>
      </div>
    </div>
  );
}

function getErrorLabel(error: string): string {
  const labels: Record<string, string> = {
    state_expired: "Login session expired. Please try again.",
    state_mismatch: "Security check failed. Please try again.",
    provider_mismatch: "Provider mismatch. Please try again.",
    missing_code: "Authorization code missing. Please try again.",
    no_email: "Could not retrieve your email. Try a different provider.",
    account_already_linked: "This account is already linked to another user.",
  };
  return labels[error] ?? "Something went wrong. Please try again.";
}
